/**
 * UTILITÁRIOS PARA COMUNICAÇÃO COM A API
 * 
 * Este arquivo contém funções utilitárias para:
 * - Tentativa de múltiplos endpoints
 * - Obtenção de tokens CSRF
 * - Geração de variações de endpoints
 * - Cache de endpoints funcionais
 */

import axios, { AxiosRequestConfig } from 'axios';
import { API_CONFIG, AXIOS_CONFIG } from '../config/api';
import { EndpointMetadata, RequestConfig } from '../types';

/**
 * Gera uma lista de endpoints corretos baseados no backend real
 * 
 * @param path Caminho do endpoint sem barras iniciais
 * @returns Lista de URLs reais que existem no backend
 */
export function generateEndpointVariations(path: string): string[] {
  const baseUrl = API_CONFIG.BASE_URL;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Apenas endpoints que realmente existem no backend
  const variants = [
    `${baseUrl}/padel/${cleanPath}`,              // Endpoint correto
    `${baseUrl}/padel/${cleanPath}/`,             // Com trailing slash
  ];
  
  // Remover duplicatas e URLs inválidas
  return [...new Set(variants)].filter(url => url && !url.includes('undefined'));
}

/**
 * Utilitário para tentar múltiplos endpoints em sequência
 * 
 * @param endpointKey Identificador único para cache
 * @param possibleEndpoints Lista de endpoints para tentar
 * @param config Configuração da requisição
 * @param method Método HTTP
 * @returns Dados da resposta ou null se falhar
 */
export async function tryMultipleEndpoints<T>(
  endpointKey: string,
  possibleEndpoints: string[],
  config: RequestConfig = {},
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET'
): Promise<T | null> {
  // Verificar se já temos um endpoint que funciona em cache
  const savedEndpoint = getSavedEndpoint(endpointKey);
  
  if (savedEndpoint) {
    try {
      // Usando endpoint em cache
      const response = await makeRequest<T>(savedEndpoint.url, method, config);
      
      // Atualizar cache com sucesso
      updateEndpointCache(endpointKey, savedEndpoint.url);
      return response;
    } catch (error) {
      console.warn(`❌ Endpoint em cache falhou para ${endpointKey}, tentando novos...`);
      clearEndpointCache(endpointKey);
    }
  }
  
  // Tentar cada endpoint sequencialmente com timeout otimizado
  for (let i = 0; i < possibleEndpoints.length; i++) {
    const endpoint = possibleEndpoints[i];
    
    try {
      // Reduced logging - only show first attempt and failures
      
      // Timeout mais agressivo para desenvolvimento e adaptativo
      const isDevelopment = process.env.NODE_ENV === 'development';
      const isFirstTry = i === 0;
      
      // Primeiro endpoint: timeout normal, demais: mais rápido
      const defaultTimeout = isDevelopment 
        ? (isFirstTry ? 10000 : 5000)  // Dev: 10s primeiro, 5s demais
        : (isFirstTry ? 15000 : 8000); // Prod: 15s primeiro, 8s demais
      
      const timeoutConfig = {
        ...config,
        timeout: Math.min(config.timeout || defaultTimeout, defaultTimeout),
        // Melhorar handling de CORS
        withCredentials: false, // Desabilitar para evitar CORS em alguns casos
      };
      
      const response = await makeRequest<T>(endpoint, method, timeoutConfig);
      
      console.log(`✅ Endpoint ${endpoint} funcionou para ${endpointKey}!`);
      
      // Salvar endpoint funcional no cache
      saveEndpointToCache(endpointKey, endpoint);
      
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const errorCode = error.code;
        const errorMessage = error.response 
          ? `HTTP ${error.response.status}: ${error.response.statusText}`
          : error.message;
        
        if (import.meta.env.DEV) {
          console.error(`❌ ${endpointKey}: ${errorMessage}`);
        }
        
        // Análise específica de erros para melhor handling
        if (errorCode === 'ERR_NETWORK' || errorCode === 'ERR_CONNECTION_REFUSED') {
          // Network error - silently try next endpoint
          continue;
        }
        
        if (errorCode === 'ECONNABORTED' || error.message.includes('timeout')) {
          // Timeout - silently try next endpoint
          continue;
        }
        
        if (status && [404, 405].includes(status)) {
          console.log(`🔄 Endpoint não encontrado (${status}), tentando próximo...`);
          continue;
        }
        
        // Para erros de CORS, tentar próximo endpoint sem withCredentials
        if (error.message.includes('CORS') || error.message.includes('Access-Control')) {
          console.log(`🔄 Erro de CORS detectado, tentando próximo endpoint...`);
          continue;
        }
        
        // Se chegou aqui e é o último endpoint, propagar o erro
        if (i === possibleEndpoints.length - 1) {
          throw error;
        }
      } else {
        console.error(`❌ Erro desconhecido em ${endpoint}:`, error);
        
        // Se é o último endpoint, propagar erro
        if (i === possibleEndpoints.length - 1) {
          throw error;
        }
      }
    }
  }
  
  if (import.meta.env.DEV) {
    console.error(`❌ All ${endpointKey} endpoints failed`);
  }
  return null;
}

/**
 * Faz uma requisição HTTP com configurações padronizadas e melhor handling de CORS
 */
async function makeRequest<T>(
  url: string,
  method: string,
  config: RequestConfig
): Promise<T> {
  // Headers básicos (remover headers problemáticos para CORS)
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };
  
  // Adicionar headers customizados apenas se não causarem CORS
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      // Filtrar headers que podem causar CORS
      const problematicHeaders = ['x-frontend-version', 'x-requested-with'];
      if (!problematicHeaders.includes(key.toLowerCase())) {
        headers[key] = value;
      }
    });
  }

  // Adicionar token CSRF apenas se necessário e disponível
  if (method !== 'GET') {
    try {
      const csrfToken = await getCSRFToken();
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }
    } catch (error) {
      // Não falhar a requisição se CSRF não estiver disponível
      console.warn('⚠️ CSRF token não disponível, continuando sem ele');
    }
  }

  const requestConfig: AxiosRequestConfig = {
    method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url,
    headers,
    timeout: config.timeout || AXIOS_CONFIG.timeout,
    withCredentials: config.withCredentials ?? false, // Padrão false para evitar CORS
    data: config.data,
    
    // Melhor handling de responses
    validateStatus: (status: number) => {
      // Aceitar 2xx e alguns 4xx para melhor error handling
      return (status >= 200 && status < 300) || [401, 403, 404].includes(status);
    },
  };

  const response = await axios(requestConfig);
  return response.data;
}

/**
 * Obtém o token CSRF do servidor (v3.2.0 - Novos endpoints implementados)
 */
export async function getCSRFToken(): Promise<string | null> {
  try {
    // Endpoints CSRF conforme backend v3.2.0 implementado
    const csrfEndpoints = [
      `${API_CONFIG.BASE_URL}/backend/user/csrf-token/`,  // Novo endpoint v3.2.0
      `${API_CONFIG.BASE_URL}/padel/get-csrf-token/`,     // Legacy
      `${API_CONFIG.BASE_URL}/backend/padel/get-csrf-token/`, // Fallback
    ];
    
    console.log('🔑 [CSRF] Iniciando obtenção de token CSRF...');
    console.log('ℹ️ [CSRF] Sistema de fallback: tentará múltiplos endpoints automaticamente');
    console.log('ℹ️ [CSRF] Erros 404 nos primeiros endpoints são NORMAIS e esperados');
    
    for (let i = 0; i < csrfEndpoints.length; i++) {
      const endpoint = csrfEndpoints[i];
      const isFirstEndpoint = i === 0;
      const isLastEndpoint = i === csrfEndpoints.length - 1;
      
      try {
        if (isFirstEndpoint) {
          console.log(`🔑 [CSRF] Tentando endpoint principal: ${endpoint}`);
        } else {
          console.log(`🔄 [CSRF] Tentando endpoint de fallback: ${endpoint}`);
        }
        
        const response = await axios.get(endpoint, {
          withCredentials: false, // Evitar CORS em CSRF
          timeout: 5000,
          headers: {
            'Accept': 'application/json',
          }
        });
        
        // Múltiplos formatos de resposta possíveis
        const token = response.data?.csrfToken || 
                     response.data?.csrf_token || 
                     response.data?.token ||
                     response.data;
        
        if (token && typeof token === 'string') {
          console.log(`✅ [CSRF] Token obtido com sucesso de: ${endpoint}`);
          console.log('🎯 [CSRF] Sistema de fallback funcionou perfeitamente!');
          return token;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          
          if (status === 404 && !isLastEndpoint) {
            console.log(`ℹ️ [CSRF] Endpoint ${endpoint} retornou 404 - NORMAL (tentando próximo)`);
            console.log('💡 [CSRF] Este comportamento é esperado no sistema de fallback');
          } else if (isLastEndpoint) {
            console.warn(`⚠️ [CSRF] Último endpoint também falhou: ${error.message}`);
          } else {
            console.warn(`⚠️ [CSRF] Falha em ${endpoint}: ${error.message} (tentando próximo)`);
          }
        }
        // Continuar para próximo endpoint
      }
    }
    
    console.warn('❌ [CSRF] Não foi possível obter token CSRF de nenhum endpoint');
    console.log('ℹ️ [CSRF] Isso pode ser normal se o backend não exigir CSRF para esta operação');
    return null;
  } catch (error) {
    console.error('❌ [CSRF] Erro inesperado ao obter CSRF token:', error);
    return null;
  }
}

/**
 * Gerenciamento de cache de endpoints
 */
const CACHE_KEY_PREFIX = 'padelmaster_endpoint_';
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 horas

function getSavedEndpoint(key: string): EndpointMetadata | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
    if (!cached) return null;
    
    const endpoint: EndpointMetadata = JSON.parse(cached);
    
    // Verificar se não está muito antigo
    if (Date.now() - endpoint.lastSuccess > MAX_CACHE_AGE) {
      clearEndpointCache(key);
      return null;
    }
    
    return endpoint;
  } catch (error) {
    clearEndpointCache(key);
    return null;
  }
}

function saveEndpointToCache(key: string, url: string): void {
  const metadata: EndpointMetadata = {
    url,
    lastSuccess: Date.now(),
    successCount: 1,
  };
  
  localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(metadata));
}

function updateEndpointCache(key: string, url: string): void {
  const existing = getSavedEndpoint(key);
  if (existing) {
    existing.lastSuccess = Date.now();
    existing.successCount++;
    localStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(existing));
  } else {
    saveEndpointToCache(key, url);
  }
}

function clearEndpointCache(key: string): void {
  localStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
}

/**
 * Limpa todo o cache de endpoints (útil para debugging)
 */
export function clearAllEndpointCache(): void {
  const keys = Object.keys(localStorage).filter(key => 
    key.startsWith(CACHE_KEY_PREFIX)
  );
  
  keys.forEach(key => localStorage.removeItem(key));
  console.log(`🧹 Cache de endpoints limpo (${keys.length} entradas removidas)`);
}
